import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { Dietitian } from '../domain/dietitian';
import { InAppPurchase } from '../domain/subscription/in-app-purchase';

export interface InAppPurchaseSchema {
  paymentIntentId: string;
  customerId: string;
  dietitianId: string | null;
  productId: string;
  invoiceId: string | null;
  description: string;
  amount: number;
  currency: string;
  createdAt: Timestamp;
}

@Injectable()
export class InAppPurchaseRepository {
  constructor(private firestore: AngularFirestore) {
    // Nada
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<InAppPurchaseSchema>(
      'in-app-purchase',
      queryFn,
    );
  }

  private toSchema(entity: InAppPurchase): InAppPurchaseSchema {
    return <InAppPurchaseSchema>{
      paymentIntentId: entity.paymentIntentId,
      customerId: entity.customerId,
      dietitianId: entity.dietitianId ?? null,
      productId: entity.productId,
      invoiceId: entity.invoiceId ?? null,
      description: entity.description,
      amount: entity.amount,
      currency: entity.currency,
      createdAt: entity.createdAt
        ? Timestamp.fromDate(entity.createdAt)
        : Timestamp.now(),
    };
  }

  fromSchema(schema: InAppPurchaseSchema, id: string): InAppPurchase {
    return InAppPurchase.create(
      {
        paymentIntentId: schema.paymentIntentId,
        customerId: schema.customerId,
        dietitianId: schema.dietitianId ?? undefined,
        productId: schema.productId,
        invoiceId: schema.invoiceId ?? undefined,
        description: schema.description,
        amount: schema.amount,
        currency: schema.currency,
        createdAt: schema.createdAt.toDate(),
      },
      new UniqueEntityID(id),
    );
  }

  async findAll(dietitian: Dietitian): Promise<InAppPurchase[]> {
    try {
      const snap = await firstValueFrom(
        this.collection((ref) =>
          ref
            .where('dietitianId', '==', dietitian.id.toString())
            .orderBy('createdAt', 'desc'),
        ).get(),
      );
      return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
    } catch (e) {
      console.error(e);
      throw e;
    }
  }
}
